/**
 * Alerts: merged Notifications + Automations. Rules tab = automation rules (CRUD, templates).
 * Activity tab = notification event log (get_notification_activity). Flow builder at /app/automations/new/flow and /app/automations/:id/flow.
 */
import { useState, useMemo } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Plus,
  ListChecks,
  Bell,
  ChevronRight,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  LayoutGrid,
  WifiOff,
  Wifi,
  Battery,
  Moon,
  BatteryWarning,
  HeartPulse,
  FileText,
  Sun,
  Zap,
  BarChart,
  Calendar,
  Eye,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useApp } from '@/contexts/AppContext';
import {
  useAutomationRules,
  useCreateAutomationRule,
  useUpdateAutomationRule,
  useDeleteAutomationRule,
  type AutomationRule,
  type AutomationRuleInsert,
  type ScopeType,
} from '@/hooks/useAutomationRules';
import {
  useAutomationTemplates,
  type AutomationTemplate,
} from '@/hooks/useAutomationTemplates';
import { useAutomationActivity } from '@/hooks/useAutomationActivity';
import {
  useNotificationActivity,
  type NotificationActivityRow,
} from '@/hooks/useNotificationActivity';
import { useNotificationEventDetail } from '@/hooks/useNotificationEventDetail';
import {
  getTriggerLabel,
  getActionLabel,
  CATEGORY_LABELS,
  BUILDABLE_TRIGGER_TYPES,
} from '@/lib/automation-trigger-types';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { PendingApprovals } from '@/components/automations/PendingApprovals';

const ICON_MAP: Record<string, React.ReactNode> = {
  'wifi-off': <WifiOff className="h-5 w-5" />,
  wifi: <Wifi className="h-5 w-5" />,
  battery: <Battery className="h-5 w-5" />,
  moon: <Moon className="h-5 w-5" />,
  'battery-warning': <BatteryWarning className="h-5 w-5" />,
  'heart-pulse': <HeartPulse className="h-5 w-5" />,
  'file-text': <FileText className="h-5 w-5" />,
  sun: <Sun className="h-5 w-5" />,
  zap: <Zap className="h-5 w-5" />,
  'bar-chart': <BarChart className="h-5 w-5" />,
  calendar: <Calendar className="h-5 w-5" />,
};

function getTemplateIcon(icon: string | null): React.ReactNode {
  return icon && ICON_MAP[icon] ? ICON_MAP[icon] : <Zap className="h-5 w-5" />;
}

function buildTriggerSentence(triggerType: string, triggerConfig?: Record<string, unknown>): string {
  const label = getTriggerLabel(triggerType);
  if (triggerType === 'system_offline' || triggerType === 'data_stale_low_energy') {
    const mins = (triggerConfig?.duration_minutes as number) ?? 30;
    return `${label} for ${mins}+ minutes`;
  }
  if (['overnight_risk', 'forecast_confidence_low', 'resilience_risk', 'resilience_risk_critical'].includes(triggerType)) {
    const conf = (triggerConfig?.min_confidence as string) ?? 'medium';
    return `${label} (confidence >= ${conf})`;
  }
  return label;
}

function scopeLabel(scope: ScopeType, propertyIds: string[], properties: { id: string; name: string }[]): string {
  if (scope === 'all') return 'All Properties';
  if (scope === 'selected') {
    if (!propertyIds?.length) return 'No properties selected';
    return propertyIds.length === 1
      ? (properties.find((p) => p.id === propertyIds[0])?.name ?? propertyIds[0].slice(0, 8))
      : `${propertyIds.length} Properties`;
  }
  return 'Tagged';
}

const TEMPLATE_CATEGORY_META: Record<string, { label: string; icon: React.ReactNode }> = {
  monitoring: { label: 'Monitoring', icon: <Bell className="h-4 w-4" /> },
  battery_forecast: { label: 'Battery & Forecast', icon: <Battery className="h-4 w-4" /> },
  solar_optimization: { label: 'Solar & Optimization', icon: <Sun className="h-4 w-4" /> },
  reporting: { label: 'Reporting', icon: <FileText className="h-4 w-4" /> },
};

function useLastTriggeredByRule(): Record<string, string> {
  const { activity } = useAutomationActivity({ limit: 500 });
  return useMemo(() => {
    const byRule: Record<string, string> = {};
    for (const row of activity) {
      const existing = byRule[row.rule_id];
      if (!existing || row.triggered_at > existing) {
        byRule[row.rule_id] = row.triggered_at;
      }
    }
    return byRule;
  }, [activity]);
}

function notificationOutcomeBadge(outcome: string) {
  if (outcome === 'sent') return <Badge variant="default">Sent</Badge>;
  if (outcome === 'suppressed') return <Badge variant="secondary">Suppressed</Badge>;
  if (outcome === 'resolved') return <Badge variant="outline">Resolved</Badge>;
  return <Badge variant="outline">{outcome}</Badge>;
}

function TemplateCard({ template, onUse }: { template: AutomationTemplate; onUse: () => void }) {
  const triggerInfo = BUILDABLE_TRIGGER_TYPES.find((t) => t.value === template.default_trigger_type);
  return (
    <div className="rounded-lg border border-border/50 bg-wt-bg-primary p-4 transition-colors hover:border-border">
      <div className="flex items-start gap-3">
        <div className="text-wt-text-muted shrink-0 mt-0.5">{getTemplateIcon(template.icon)}</div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm text-wt-text-primary truncate">{template.name}</h4>
          <p className="text-xs text-wt-text-muted mt-1 line-clamp-2">{template.description}</p>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {triggerInfo && (
              <span
                className={cn(
                  'inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium',
                  triggerInfo.severity === 'critical' && 'bg-wt-red-primary/15 text-wt-red-primary border border-wt-red-primary/30',
                  triggerInfo.severity === 'warning' && 'bg-wt-amber-primary/15 text-wt-amber-primary border border-wt-amber-primary/30',
                  triggerInfo.severity === 'info' && 'bg-wt-green-primary/15 text-wt-green-primary border border-wt-green-primary/30'
                )}
              >
                {triggerInfo.severity}
              </span>
            )}
            {template.is_dag && (
              <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-wt-green-primary/15 text-wt-green-primary border border-wt-green-primary/30">
                Flow
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-wt-border-subtle flex justify-end">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onUse}>
          Use template
        </Button>
      </div>
    </div>
  );
}

export default function Alerts() {
  const navigate = useNavigate();
  const { properties, isInstaller, navigationScope } = useApp();
  const scopedPropertyId = navigationScope.type === 'property' ? navigationScope.propertyId : undefined;
  const [activeTab, setActiveTab] = useState('rules');
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [activityFilters, setActivityFilters] = useState<{
    dateFrom?: string;
    dateTo?: string;
    propertyId?: string;
    eventKind?: string;
    outcome?: string;
    audienceType?: string | null;
  }>({ propertyId: scopedPropertyId });
  const [deleteRuleId, setDeleteRuleId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const { rules: allRules, isLoading: rulesLoading, refetch: refetchRules } = useAutomationRules();
  // When scoped to a property, filter rules that apply to it (scope_type 'all' or property in scope_property_ids)
  const rules = scopedPropertyId
    ? allRules.filter(r => r.scope_type === 'all' || r.scope_property_ids.includes(scopedPropertyId))
    : allRules;
  const { templates, isLoading: templatesLoading } = useAutomationTemplates();
  const lastTriggeredByRule = useLastTriggeredByRule();
  const { detail: eventDetail, isLoading: detailLoading } = useNotificationEventDetail(selectedEventId);
  const { activity, isLoading: activityLoading, refetch: refetchActivity } = useNotificationActivity({
    dateFrom: activityFilters.dateFrom,
    dateTo: activityFilters.dateTo,
    propertyId: activityFilters.propertyId,
    eventKind: activityFilters.eventKind,
    outcome: activityFilters.outcome,
    audienceType: activityFilters.audienceType ?? null,
    limit: 100,
  });

  const createRule = useCreateAutomationRule();
  const updateRule = useUpdateAutomationRule();
  const deleteRule = useDeleteAutomationRule();

  const handleDuplicate = async (rule: AutomationRule) => {
    const insert: AutomationRuleInsert = {
      name: `Copy of ${rule.name}`,
      description: rule.description ?? undefined,
      is_enabled: false,
      trigger_type: rule.trigger_type,
      trigger_config: rule.trigger_config ?? {},
      scope_type: rule.scope_type,
      scope_property_ids: rule.scope_property_ids ?? [],
      scope_tags: rule.scope_tags ?? [],
      schedule_type: rule.schedule_type ?? 'always',
      schedule_config: rule.schedule_config ?? null,
      action_type: rule.action_type,
      action_config: rule.action_config ?? {},
      cooldown_minutes: rule.cooldown_minutes ?? 120,
      max_executions_per_day: rule.max_executions_per_day ?? null,
      is_dag: true,
      dag_definition: rule.dag_definition,
    };
    try {
      await createRule.mutateAsync(insert);
      toast.success('Rule duplicated');
      refetchRules();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to duplicate');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteRule.mutateAsync(id);
      toast.success('Rule deleted');
      setDeleteRuleId(null);
      refetchRules();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const handleUseTemplate = async (template: AutomationTemplate) => {
    try {
      // All rules are DAG now. If template has no dag_definition, generate a simple one.
      const dagDef = template.dag_definition ?? {
        nodes: [
          { id: 'trigger-1', type: 'trigger', position: { x: 0, y: 0 }, config: { title: template.name, trigger_type: template.default_trigger_type, trigger_config: template.default_trigger_config ?? {}, scope_type: 'all', scope_property_ids: [], scope_tags: [] } },
          { id: 'action-1', type: 'action_block', position: { x: 0, y: 200 }, config: { title: 'Action', steps: [{ id: 'step-1', type: 'action', action_type: template.default_action_type, action_config: template.default_action_config ?? {}, title: template.default_action_type }] } },
          { id: 'end-1', type: 'end', position: { x: 0, y: 400 }, config: {} },
        ],
        edges: [{ from: 'trigger-1', to: 'action-1' }, { from: 'action-1', to: 'end-1' }],
      };

      const result = await createRule.mutateAsync({
        name: template.name,
        description: template.description,
        is_enabled: false,
        trigger_type: template.default_trigger_type,
        trigger_config: template.default_trigger_config ?? {},
        scope_type: 'all',
        scope_property_ids: [],
        scope_tags: [],
        schedule_type: 'always',
        schedule_config: null,
        action_type: template.default_action_type as AutomationRuleInsert['action_type'],
        action_config: template.default_action_config ?? {},
        cooldown_minutes: template.default_cooldown_minutes ?? 120,
        is_dag: true,
        dag_definition: dagDef,
        template_id: template.id,
      });
      toast.success('Rule created from template. Activate it from the list or open to edit.');
      setTemplatesOpen(false);
      refetchRules();
      if (result?.id) {
        navigate(`/app/automations/${result.id}/flow`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create rule');
    }
  };

  if (!isInstaller) {
    return <Navigate to="/app" replace />;
  }

  const proposalTemplates = templates.filter((t) => t.default_action_type === 'proposal');
  const categoryOrder = ['monitoring', 'battery_forecast', 'solar_optimization', 'reporting'] as const;
  const templatesByCategory = (cat: string) =>
    templates.filter((t) => t.category === cat && t.default_action_type !== 'proposal');
  const hasProposals = proposalTemplates.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-wt-text-primary">
            {scopedPropertyId ? `${navigationScope.propertyName} Automations` : 'Automations'}
          </h1>
          <p className="text-wt-text-muted text-sm">
            {scopedPropertyId
              ? `Rules and activity for ${navigationScope.propertyName}.`
              : 'Configure automation rules and flows; view execution activity.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setTemplatesOpen(true)}>
            <LayoutGrid className="mr-2 h-4 w-4" />
            Browse Templates
          </Button>
          <Button size="sm" asChild>
            <Link to="/app/automations/new/flow">
              <Plus className="mr-2 h-4 w-4" />
              New Automation
            </Link>
          </Button>
        </div>
      </div>

      <PendingApprovals />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="rules">
            <ListChecks className="mr-2 h-4 w-4" />
            Rules
          </TabsTrigger>
          <TabsTrigger value="activity">
            <Bell className="mr-2 h-4 w-4" />
            Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="space-y-3 mt-4">
          {rulesLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-lg border border-border/50 bg-wt-bg-secondary px-4 py-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-2.5 w-2.5 rounded-full" />
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-5 w-20 ml-auto" />
                  </div>
                  <Skeleton className="h-3 w-64" />
                  <Skeleton className="h-3 w-40" />
                </div>
              ))}
            </div>
          ) : rules.length === 0 ? (
            <div className="rounded-lg border border-border/50 bg-wt-bg-secondary px-6 py-12 text-center">
              <p className="text-wt-text-muted text-sm">
                No rules yet. Create your first rule or browse templates to get started.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setTemplatesOpen(true)}>
                  Browse Templates
                </Button>
                <Button size="sm" asChild>
                  <Link to="/app/automations/new/flow">New Automation</Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((rule) => {
                const lastTriggered = lastTriggeredByRule[rule.id];
                return (
                  <div
                    key={rule.id}
                    className={cn(
                      'rounded-lg border border-border/50 bg-wt-bg-secondary px-4 py-3 transition-colors duration-150',
                      'hover:border-border hover:bg-wt-bg-tertiary/50',
                      rule.is_enabled
                        ? 'border-l-[3px] border-l-wt-green-primary/40'
                        : 'opacity-75 border-l-[3px] border-l-wt-border-subtle'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'h-2.5 w-2.5 rounded-full shrink-0',
                          rule.is_enabled ? 'bg-status-online' : 'bg-wt-text-disabled'
                        )}
                      />
                      <span className="font-semibold text-sm text-wt-text-primary truncate flex-1 min-w-0">
                        {rule.name}
                      </span>
                      {rule.is_dag && (
                        <Badge variant="outline" className="text-[10px] h-5 bg-wt-green-primary/10 border-wt-green-primary/20 text-wt-green-primary shrink-0">
                          Flow
                        </Badge>
                      )}
                      <div className="flex items-center gap-2 shrink-0">
                        <Switch
                          checked={rule.is_enabled}
                          onCheckedChange={() => {
                            updateRule.mutate({ id: rule.id, updates: { is_enabled: !rule.is_enabled } });
                          }}
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/app/automations/${rule.id}/flow`}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(rule)}>
                              <Copy className="mr-2 h-4 w-4" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-wt-red-primary focus:text-wt-red-primary"
                              onClick={() => setDeleteRuleId(rule.id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 text-xs text-wt-text-secondary">
                      <span className="text-wt-text-muted">WHEN</span>
                      {buildTriggerSentence(rule.trigger_type, rule.trigger_config as Record<string, unknown>)}
                      <ChevronRight className="h-3 w-3 text-wt-text-muted shrink-0" />
                      <span className="text-wt-text-muted">THEN</span>
                      {getActionLabel(rule.action_type)}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-wt-text-muted">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-wt-bg-tertiary border border-wt-border-subtle">
                        {scopeLabel(rule.scope_type, rule.scope_property_ids ?? [], properties)}
                      </span>
                      <span>
                        {lastTriggered
                          ? `Triggered ${formatDistanceToNow(new Date(lastTriggered), { addSuffix: true })}`
                          : 'Never triggered'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="activity" className="space-y-4 mt-4">
          <div className="rounded-lg border border-border/50 bg-wt-bg-secondary px-4 py-3">
            <div className="flex flex-wrap gap-2">
              <Input
                type="date"
                className="w-40"
                value={activityFilters.dateFrom ?? ''}
                onChange={(e) => setActivityFilters((f) => ({ ...f, dateFrom: e.target.value || undefined }))}
              />
              <Input
                type="date"
                className="w-40"
                value={activityFilters.dateTo ?? ''}
                onChange={(e) => setActivityFilters((f) => ({ ...f, dateTo: e.target.value || undefined }))}
              />
              <Select
                value={activityFilters.propertyId ?? 'all'}
                onValueChange={(v) => setActivityFilters((f) => ({ ...f, propertyId: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Property" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All properties</SelectItem>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={activityFilters.outcome ?? 'all'}
                onValueChange={(v) => setActivityFilters((f) => ({ ...f, outcome: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="suppressed">Suppressed</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={activityFilters.audienceType ?? 'all'}
                onValueChange={(v) => setActivityFilters((f) => ({ ...f, audienceType: v === 'all' ? null : v }))}
              >
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Audience" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="installer">Installer</SelectItem>
                  <SelectItem value="homeowner">Homeowner</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => refetchActivity()}>
                Refresh
              </Button>
            </div>
          </div>
          {activityLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : activity.length === 0 ? (
            <p className="text-wt-text-muted py-6 text-center">No activity in the selected range.</p>
          ) : (
            <div className="rounded-lg border border-border/50 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-wt-bg-secondary">
                    <TableHead className="text-wt-text-muted">Time</TableHead>
                    <TableHead className="text-wt-text-muted">Property</TableHead>
                    <TableHead className="text-wt-text-muted">Event</TableHead>
                    <TableHead className="text-wt-text-muted">Severity</TableHead>
                    <TableHead className="text-wt-text-muted">Audience</TableHead>
                    <TableHead className="text-wt-text-muted">Outcome</TableHead>
                    <TableHead className="text-wt-text-muted">Why</TableHead>
                    <TableHead className="text-wt-text-muted w-[80px]">View</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activity.map((row: NotificationActivityRow) => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
                      </TableCell>
                      <TableCell>
                        <Link to={`/app/property/${row.property_id}`} className="text-primary hover:underline">
                          {row.property_name}
                        </Link>
                      </TableCell>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>
                        <Badge variant={row.severity === 'critical' ? 'destructive' : row.severity === 'warning' ? 'secondary' : 'outline'}>
                          {row.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{row.audience_type ?? 'installer'}</Badge>
                      </TableCell>
                      <TableCell>{notificationOutcomeBadge(row.outcome)}</TableCell>
                      <TableCell className="text-muted-foreground max-w-[200px] truncate" title={row.suppression_reason ?? ''}>
                        {row.suppression_reason ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedEventId(row.id)}
                          title="View message and delivery"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Notification event detail dialog */}
      <Dialog open={!!selectedEventId} onOpenChange={(open) => !open && setSelectedEventId(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Notification details</DialogTitle>
            <DialogDescription>Message and delivery status for this event.</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : eventDetail ? (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Title</p>
                <p className="font-medium mt-0.5">{eventDetail.title}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Message</p>
                <p className="text-sm mt-0.5 whitespace-pre-wrap">{eventDetail.message || '—'}</p>
              </div>
              {eventDetail.context && Object.keys(eventDetail.context).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Context</p>
                  <pre className="text-xs mt-0.5 p-3 rounded-md bg-muted overflow-x-auto max-h-32 overflow-y-auto">
                    {JSON.stringify(eventDetail.context, null, 2)}
                  </pre>
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Delivery</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center justify-between gap-2">
                    <span>In-app</span>
                    <span className="text-muted-foreground">Always recorded</span>
                  </li>
                  <li className="flex items-center justify-between gap-2">
                    <span>Email</span>
                    {eventDetail.delivered_email_at ? (
                      <span className="text-muted-foreground">Sent {formatDistanceToNow(new Date(eventDetail.delivered_email_at), { addSuffix: true })}</span>
                    ) : eventDetail.email_error_text ? (
                      <span className="text-destructive truncate max-w-[200px]" title={eventDetail.email_error_text}>Failed</span>
                    ) : eventDetail.outcome === 'suppressed' ? (
                      <span className="text-muted-foreground">Not sent (suppressed)</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </li>
                  <li className="flex items-center justify-between gap-2">
                    <span>SMS</span>
                    {eventDetail.delivered_sms_at ? (
                      <span className="text-muted-foreground">Sent {formatDistanceToNow(new Date(eventDetail.delivered_sms_at), { addSuffix: true })}</span>
                    ) : eventDetail.sms_error_text ? (
                      <span className="text-destructive truncate max-w-[200px]" title={eventDetail.sms_error_text}>Failed</span>
                    ) : eventDetail.outcome === 'suppressed' ? (
                      <span className="text-muted-foreground">Not sent (suppressed)</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </li>
                  <li className="flex items-center justify-between gap-2">
                    <span>Web Push</span>
                    {eventDetail.delivered_push_at ? (
                      <span className="text-muted-foreground">Sent {formatDistanceToNow(new Date(eventDetail.delivered_push_at), { addSuffix: true })}</span>
                    ) : eventDetail.push_error_text ? (
                      <span className="text-destructive truncate max-w-[200px]" title={eventDetail.push_error_text}>Failed</span>
                    ) : eventDetail.outcome === 'suppressed' ? (
                      <span className="text-muted-foreground">Not sent (suppressed)</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </li>
                </ul>
                {eventDetail.channels?.length ? (
                  <p className="text-xs text-muted-foreground mt-2">Requested channels: {eventDetail.channels.join(', ')}</p>
                ) : null}
              </div>
            </div>
          ) : selectedEventId ? (
            <p className="text-muted-foreground py-4">Could not load event details.</p>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Template gallery sheet */}
      <Sheet open={templatesOpen} onOpenChange={setTemplatesOpen}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-xl bg-wt-bg-secondary border-wt-border-default">
          <SheetHeader>
            <SheetTitle className="text-wt-text-primary">Browse Templates</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-8">
            {templatesLoading ? (
              <div className="space-y-3">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-24 rounded-lg" />
                ))}
              </div>
            ) : (
              <>
                {hasProposals && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-wt-text-muted text-sm font-medium">Proposals</span>
                      <div className="flex-1 h-px bg-wt-border-subtle" />
                    </div>
                    <div className="grid gap-3">
                      {proposalTemplates.map((t) => (
                        <TemplateCard key={t.id} template={t} onUse={() => handleUseTemplate(t)} />
                      ))}
                    </div>
                  </div>
                )}
                {categoryOrder.map((cat) => {
                  const items = templatesByCategory(cat);
                  if (items.length === 0) return null;
                  const meta = TEMPLATE_CATEGORY_META[cat];
                  return (
                    <div key={cat}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-wt-text-muted">{meta?.icon}</span>
                        <span className="text-wt-text-muted text-sm font-medium">
                          {meta?.label ?? CATEGORY_LABELS[cat] ?? cat}
                        </span>
                        <div className="flex-1 h-px bg-wt-border-subtle" />
                      </div>
                      <div className="grid gap-3">
                        {items.map((t) => (
                          <TemplateCard key={t.id} template={t} onUse={() => handleUseTemplate(t)} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={Boolean(deleteRuleId)} onOpenChange={(open) => !open && setDeleteRuleId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete automation rule?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRuleId && rules.find((r) => r.id === deleteRuleId) && (
                <>
                  This will permanently delete <strong>{rules.find((r) => r.id === deleteRuleId)?.name}</strong>. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteRuleId && handleDelete(deleteRuleId)}
              disabled={deleteRule.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
